/**
 * RESPONSIBILITY: one ministry — its card, and the page that card opens.
 *
 * Both, deliberately. A ministry was a card that linked wherever somebody typed, so every
 * card went to the same page. The card and the page are the same ministry, so they are
 * the same row: fill it in once and the grid, the link and the page all follow.
 *
 * The page is reached by ROW ID, so a link survives the list being reordered.
 */
import type { ImageValue } from '@framework/core';
import { Row, Text, Paragraph, Img, Bool, Link, List, RenderOf } from '@framework/core/decorators';
import { MinistryBand } from './ministry-band';

@Row()
export class Ministry {
  @Text({ label: 'Title', maxLength: 120, description: 'The ministry’s name.' })
  title!: string;

  /**
   * On unless somebody says otherwise, and read as `show !== false` — see commons/shown.
   * A bool with no default falls to false, which would have hidden every ministry
   * already on the site the moment this field was added.
   */
  @Bool({
    label: 'Show this ministry', default: true, group: 'Where it appears', collapsed: true,
    description: 'Untick to take it off Home and the Ministries page. You still see it, marked.',
  })
  show!: boolean;

  @Text({
    label: 'Description', maxLength: 600,
    description: 'The line under the title on the card.',
  })
  blurb!: string;

  /**
   * ONE picture, for the card and for the page it opens.
   *
   * There were two, and seven of the eight ministries held the same file in both — until
   * somebody uploaded a photograph of the mission team, put it in the card, and the page
   * went on showing the placeholder it was seeded with. Two fields for one picture means
   * the page and the card can disagree, and the moment they do, one of them is wrong
   * with nothing to say which.
   */
  @Img({
    label: 'Picture', folder: 'ministries', maxSizeMb: 12, size: '1600 × 900',
    description: 'On the card, and across the top of this ministry’s own page.',
  })
  image!: ImageValue;

  /* ---- the page it opens ---- */

  /**
   * EMPTY MEANS "use the one above", and empty is how every ministry starts.
   *
   * This was once a second picture with no such rule, seeded with a placeholder. So a
   * ministry that looked like it had one picture had two, the card showed the good one
   * and the page went on showing the placeholder, and nothing in the menu said why.
   * Empty-or-not is one value with one meaning; a picture here is a deliberate choice to
   * differ, and there is no second switch that can disagree with it.
   */
  @Img({
    label: 'A different picture for its page', folder: 'ministries', maxSizeMb: 12,
    size: '1600 × 900', group: 'Its page', collapsed: true,
    description: 'Leave empty and the page uses the picture above. Fill it in only to differ.',
  })
  heroImage!: ImageValue;

  @Paragraph({
    label: 'About this ministry', maxLength: 20000, rows: 8,
    markdown: true, allow: ['verse'], group: 'Its page',
  })
  body!: string;

  @RenderOf('body')
  bodyHtml!: string;

  @List({
    label: 'Bands down the page', of: () => MinistryBand, maxItems: 20,
    description:
      'Each is a band down the page, built from a template you choose — a picture and '
      + 'its words, a list of questions, a gallery, a video, and so on. Leave empty and the '
      + 'page shows the description above on its own.',
  })
  bands!: MinistryBand[];

  @Text({
    label: 'Show events tagged', maxLength: 120, group: 'Its page',
    description: 'Which slice of the one events list this page shows. e.g. Children.',
  })
  eventTag!: string;

  /**
   * Only when the title makes a poor link. Empty is the normal case: the address is made
   * from the title, so "Regional Bible Studies" is already `regional-bible-studies`.
   */
  @Text({
    label: 'Web address', maxLength: 300, group: 'Its page',
    description: 'The last part of this page’s link. Leave empty to use the title.',
  })
  slug!: string;

  /**
   * Only for a ministry that lives somewhere else entirely. Empty is the normal case and
   * means "use the page this row already describes".
   */
  @Link({
    label: 'Link elsewhere instead', group: 'Its page',
    description: 'Leave empty unless this ministry has its own site.',
  })
  href!: string;
}
